import { useTheme } from "@/context/ThemeContext";
import { Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact theme picker — renders as a grid of preview cards.
 * Used in `/settings` (per-user) and can also be dropped into
 * `/superadmin` for workspace default (persist=false there).
 */
export default function ThemePicker({ persist = true, onChange }) {
  const { themeId, setTheme, themes } = useTheme();

  const pick = async (id) => {
    await setTheme(id, { persist });
    if (onChange) onChange(id);
    if (persist) toast.success(`Theme changed to ${themes.find((t) => t.id === id)?.name || id}`);
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="theme-picker">
      {themes.map((t) => {
        const isActive = t.id === themeId;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => pick(t.id)}
            data-testid={`theme-option-${t.id}`}
            data-active={isActive ? "1" : "0"}
            className={`text-left border-2 p-4 transition-all hover:-translate-y-0.5 ${
              isActive
                ? "border-[var(--peak-accent)] shadow-[6px_6px_0_0_var(--peak-shadow)]"
                : "border-[var(--peak-ink)] hover:shadow-[4px_4px_0_0_var(--peak-shadow)]"
            } bg-[var(--peak-surface)]`}
          >
            {/* Live swatch preview */}
            <div className="flex h-14 border-2 border-[var(--peak-ink)] overflow-hidden mb-3">
              {t.swatches.map((c, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: c }} />
              ))}
            </div>

            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading font-black tracking-tight text-base leading-tight text-[var(--peak-ink)]">
                  {t.name}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-1">
                  {t.vibe}
                </div>
                <div className="text-xs text-[var(--peak-muted)] mt-2 leading-relaxed">
                  {t.tagline}
                </div>
              </div>
              {isActive && (
                <div
                  className="flex-none w-6 h-6 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] flex items-center justify-center"
                  data-testid={`theme-active-${t.id}`}
                >
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
